import Vue from "vue";
import Vuex from "vuex";
import sortBy from "lodash/sortBy";
import {MUTATION_NAME, PLATFORMS} from "@/static/global-variables";
import validatorUtil from "@/utils/validator-util";
import services from "@/services";
import distributionChannelUtil from "@/utils/distribution-channel-util";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    username: null,
    userLocale: null,
    userTimeZone: null,
    crmServerURL: null,
    crmServerVersion: null,
    mcServerURL: null,
    mcServerVersion: null,
    territoryServerURL: null,
    territoryServerVersion: null,
    domain: null,
    user: null,
    org: null,
    messages: null,
    apps: null,
    appTypes: null,
    objectNameLabels: null,
    htmlReportFieldLabels: null,
    objectDescribe: {},
    deleteDraggableItem: null,
    editDraggableItem: null,
    updateDraggableItem: null,
    moveDraggableItem: null,
    latestApps: null,
    currentContent: null,
    sfGlobalObjects: null,
    sfObjectsInfo: null,
    sfEntryPointOjbectsInfo: null,
    sfObjectsMeta: null,
    editDataElement: null,
    nitroDataCache: null,
    pageInfo: null,
    previewPlatform: PLATFORMS.APP,
    hasSession: false,
    distributionChannelsSummary: null,
    distributionChannelLatestWidgets: null,
    distributionChannelDetail: null,
    widgetVersions: null,
    channelWidgetVersionList: {},
    customerDomainWidgetAccess: {},
    setUpdateFlag: {},
    widgetTranslations: null,
    allWidgetTranslations: {},
    widgetTranslationRequest: {},
    addWidgetData: null,
    addNewWidgetVersion: null,
    widgetVersionDetails: null,
    allWidgetVersionDetails: {},
    customerDomains: null,
    customerDomainData: null,
    distributionChannelList: null,
    addEditWidgetData: null,
    refreshOverview: false,
    isFetchingManifests: false,
    objectLevelSecurity: null
  },
  mutations: {
    [MUTATION_NAME.SET_USERNAME]: (state, username) => {
      state.username = username || null;
    },
    [MUTATION_NAME.SET_USER_LOCALE]: (state, userLocale) => {
      state.userLocale = userLocale || null;
    },
    [MUTATION_NAME.SET_USER_TIMEZONE]: (state, userTimeZone) => {
      state.userTimeZone = userTimeZone || null;
    },
    [MUTATION_NAME.SET_CRM_SERVER_URL]: (state, crmServerURL) => {
      state.crmServerURL = crmServerURL || null;
    },
    [MUTATION_NAME.SET_CRM_SERVER_VERSION]: (state, crmServerVersion) => {
      state.crmServerVersion = crmServerVersion || null;
    },
    [MUTATION_NAME.SET_OLS]: (state, objectLevelSecurity) => {
      state.objectLevelSecurity = objectLevelSecurity || null;
    },

    [MUTATION_NAME.SET_MC_SERVER_URL]: (state, mcServerURL) => {
      state.mcServerURL = mcServerURL || null;
    },
    [MUTATION_NAME.SET_MC_SERVER_VERSION]: (state, mcServerVersion) => {
      state.mcServerVersion = mcServerVersion || null;
    },
    [MUTATION_NAME.SET_TERRITORY_SERVER_URL]: (state, territoryServerURL) => {
      state.territoryServerURL = territoryServerURL || null;
    },
    [MUTATION_NAME.SET_TERRITORY_SERVER_VERSION]: (state, territoryServerVersion) => {
      state.territoryServerVersion = territoryServerVersion || null;
    },

    [MUTATION_NAME.SET_DOMAIN]: (state, domain) => {
      state.domain = domain || null;
    },
    [MUTATION_NAME.SET_USER]: (state, user) => {
      state.user = user || null;
    },
    [MUTATION_NAME.SET_ORG]: (state, org) => {
      state.org = org || null;
    },
    [MUTATION_NAME.SET_MESSAGES]: (state, messages) => {
      state.messages = messages || null;
    },
    [MUTATION_NAME.SET_MESSAGES]: (state, messages) => {
      state.messages = messages || null;
    },
    [MUTATION_NAME.SET_APPS]: (state, apps) => {
      state.apps = apps || null;
    },
    [MUTATION_NAME.UPDATE_APP]: (state, app) => {
      if (!Array.isArray(state.apps)) {
        return;
      }

      state.apps = state.apps.map(a => {
        let output = a;

        if (a.uuid === app.uuid) {
          output = app;
          services.updateElmCompiler(output);
        }
        return output;
      });

      services.saveAppsLocally(state.apps);
    },
    [MUTATION_NAME.UPDATE_IS_DIRTY_FLAG]: (state, data) => {
      state.apps = state.apps.map(app => {
        if (app.uuid === data.appId) {
          app.isDirty = data.isDirty;
        }
        return app;
      });
      services.saveAppsLocally(state.apps);
    },
    [MUTATION_NAME.ADD_APP]: (state, app) => {
      state.apps = sortBy([app].concat(state.apps), "name");
    },
    [MUTATION_NAME.SET_APPTYPES]: (state, appTypes) => {
      state.appTypes = appTypes || null;
    },
    [MUTATION_NAME.SET_OBJECT_NAME_LABELS]: (state, objectNameLabels) => {
      state.objectNameLabels = objectNameLabels || null;
    },
    [MUTATION_NAME.SET_OBJECT_DESCRIBE]: (state, config) => {
      if (config) {
        state.objectDescribe[config.obj] = config.data;
      } else {
        state.objectDescribe = {};
      }
    },
    [MUTATION_NAME.SET_HTML_REPORT_FIELD_LABELS]: (
      state,
      htmlReportFieldLabels
    ) => {
      state.htmlReportFieldLabels = htmlReportFieldLabels || null;
    },
    [MUTATION_NAME.SET_APPS_DEPLOYMENT_SUMMARY]: (
      state,
      appsDeploymentSummary
    ) => {
      state.appsDeploymentSummary = appsDeploymentSummary || null;
    },
    [MUTATION_NAME.EDIT_DATA_PALETTES]: (state, dataElement) => {
      state.apps = state.apps.map(app => {
        if (app.uuid === dataElement.appId) {
          if (!app.dataElements) {
            app.dataElements = {};
          }

          app.isDirty = true;
          app.dataElements[dataElement.id] = dataElement;
        }

        return app;
      });

      services.saveAppsLocally(state.apps);
    },

    [MUTATION_NAME.DELETE_DATA_ELEMENT]: (state, params) => {
      state.apps = state.apps.map(app => {
        if (app.uuid === params.appId) {
          delete app.dataElements[params.deId];
          app.isDirty = true;
          services.updateElmCompiler(app);
        }

        return app;
      });

      services.saveAppsLocally(state.apps);
    },

    [MUTATION_NAME.UPDATE_DATA_ELEMENT_NAME]: (state, params) => {
      state.apps = state.apps.map(app => {
        if (app.uuid === params.appId) {
          app.dataElements[params.deId].name = params.newName;
          app.isDirty = true;
          services.updateElmCompiler(app);
        }

        return app;
      });

      services.saveAppsLocally(state.apps);
    },

    [MUTATION_NAME.UPDATE_DE_QUERY]: (state, deInfo) => {
      state.apps = state.apps.map(app => {
        if (app.uuid === deInfo.appId) {
          app.dataElements[deInfo.de.id] = deInfo.de;
          app.isDirty = true;
          services.updateElmCompiler(app);
        }

        return app;
      });

      services.saveAppsLocally(state.apps);
    },
    [MUTATION_NAME.SET_DELETE_DRAGGABLE]: (state, data) => {
      state.deleteDraggableItem = data;
    },
    [MUTATION_NAME.SET_EDIT_DRAGGABLE]: (state, data) => {
      state.editDraggableItem = data;
    },
    [MUTATION_NAME.SET_UPDATE_DRAGGABLE]: (state, data) => {
      state.updateDraggableItem = data;
    },
    [MUTATION_NAME.SET_MOVE_DRAGGABLE]: (state, data) => {
      state.moveDraggableItem = data;
    },
    [MUTATION_NAME.SET_LATEST_APPS]: (state, data) => {
      state.latestApps = data || null;
    },
    [MUTATION_NAME.SET_CURRENT_CONTENT]: (state, data) => {
      state.currentContent = null;
      state.currentContent = data;
    },
    [MUTATION_NAME.SET_SF_GLOBAL_OBJECTS]: (state, data) => {
      state.sfGlobalObjects = null;
      state.sfGlobalObjects = data;
    },
    [MUTATION_NAME.SET_NITRO_DATA_CACHE]: (state, data) => {
      state.nitroDataCache = null;
      state.nitroDataCache = data;
    },
    [MUTATION_NAME.SET_SF_OBJECTS_INFO]: (state, data) => {
      state.sfObjectsInfo = null;
      state.sfObjectsInfo = data;
    },
    [MUTATION_NAME.SET_SF_ENTRY_POINT_OBJECTS_INFO]: (state, data) => {
      state.sfEntryPointOjbectsInfo = null;
      state.sfEntryPointOjbectsInfo = data;
    },
    [MUTATION_NAME.SET_SF_OBJECTS_META]: (state, data) => {
      state.sfObjectsMeta = null;
      state.sfObjectsMeta = data;
    },
    [MUTATION_NAME.SET_EDIT_DATA_ELEMENT]: (state, data) => {
      state.editDataElement = null;
      state.editDataElement = data;
    },
    [MUTATION_NAME.SET_PAGE_INFO]: (state, data) => {
      state.pageInfo = null;
      state.pageInfo = data;
    },
    [MUTATION_NAME.SET_PREVIEW_PLATFORM]: (state, data) => {
      state.previewPlatform = data;
    },
    [MUTATION_NAME.SET_HAS_SESSION](state, data) {
      state.hasSession = data;
    },
    [MUTATION_NAME.DISTRIBUTION_CHANNELS_SUMMARY](state, data) {
      state.distributionChannelsSummary = data;
    },
    [MUTATION_NAME.UPDATE_DISTRIBUTION_CHANNEL_SUMMARY_NAME](state, data) {
      if (state.distributionChannelsSummary) {
        state.distributionChannelsSummary = state.distributionChannelsSummary.map(
          e => {
            if (e.uuid === data.uuid) {
              e.name = data.name;
            }
            return e;
          }
        );
      }
    },
    [MUTATION_NAME.DISTRIBUTION_CHANNEL_LATEST_WIDGETS](state, data) {
      state.distributionChannelLatestWidgets = data;
    },
    [MUTATION_NAME.DELETE_LATEST_UNPUBLISHED_WIDGET](state, uuid) {
      const widgets = state.distributionChannelLatestWidgets.widgets.map(e => {
        if (e.unpublished && e.unpublished.uuid === uuid) {
          e.unpublished = null;
        }
        return e;
      });
      state.distributionChannelLatestWidgets.widgets = widgets.filter(
        e => e.current || e.unpublished
      );
    },
    [MUTATION_NAME.UPDATE_LATEST_UNPUBLISHED_VERSION](state, data) {
      state.distributionChannelLatestWidgets.widgets = state.distributionChannelLatestWidgets.widgets.map(
        e => {
          if (e.uuid === data.widgetuuid) {
            e.unpublished = data.unpublished;
          }
          return e;
        }
      );
    },
    [MUTATION_NAME.SET_PUBLISHED_VERSION_LATEST_WIDGET](state, data) {
      state.distributionChannelLatestWidgets.widgets = state.distributionChannelLatestWidgets.widgets.map(
        e => {
          if (e.unpublished && e.unpublished.uuid === data.uuid) {
            e.current = {
              uuid: data.uuid,
              version: data.version
            };
            e.unpublished = null;
          }
          return e;
        }
      );
    },
    [MUTATION_NAME.DISTRIBUTION_CHANNEL_DETAIL](state, data) {
      state.distributionChannelDetail = data;
    },
    [MUTATION_NAME.UPDATE_DISTRIBUTION_CHANNEL_DETAIL](state, data) {
      state.distributionChannelDetail = {
        ...state.distributionChannelDetail,
        ...data
      };
    },
    [MUTATION_NAME.WIDGET_VERSIONS](state, data) {
      state.widgetVersions = data;
    },
    [MUTATION_NAME.UPDATE_WIDGET_UNPUBLISHED_VERSION](state, data) {
      if (
        state.widgetVersions &&
        state.widgetVersions.widgetuuid === data.widgetuuid
      ) {
        const versions = state.widgetVersions.versions.filter(e => e.published);
        state.widgetVersions.versions = [...[data.unpublished], ...versions];
      }
    },
    [MUTATION_NAME.SET_PUBLISHED_VERSION_WIDGET_VERSIONS](state, data) {
      if (state.widgetVersions) {
        state.widgetVersions.versions = state.widgetVersions.versions.map(e => {
          return e.uuid === data.uuid ? { ...data } : e;
        });
      }
    },
    [MUTATION_NAME.DELETE_UNPUBLISHED_WIDGET_VERSION](state, uuid) {
      if (state.widgetVersions && state.widgetVersions.versions) {
        state.widgetVersions.versions = state.widgetVersions.versions.filter(
          e => e.uuid !== uuid
        );
      }
    },
    [MUTATION_NAME.CHANNEL_WIDGET_VERSION_LIST](state, data) {
      const objList = state.channelWidgetVersionList;
      const distChannel = objList[data.uuid] || {};
      distChannel[data.widgetuuid] = data.versions;
      objList[data.uuid] = distChannel;
      state.channelWidgetVersionList = { ...objList };
    },
    [MUTATION_NAME.SETUP_CUSTOMER_DOMAIN_WIDGET_ACCESS](state, data) {
      state.customerDomainWidgetAccess = data;
    },
    [MUTATION_NAME.CUSTOMER_DOMAIN_WIDGET_ACCESS](state, data) {
      if (data.customer) {
        const customerAccess = state.customerDomainWidgetAccess;
        customerAccess[data.customer] = data.access || {};
        state.customerDomainWidgetAccess = { ...customerAccess };
      }
    },
    [MUTATION_NAME.WIDGET_TRANSLATIONS](state, data) {
      state.widgetTranslations = data;
    },
    [MUTATION_NAME.UPDATE_WIDGET_TRANSLATION](state, data) {
      state.widgetTranslations.translations = state.widgetTranslations.translations.map(
        e => (e.uuid === data.uuid ? data : e)
      );
    },
    [MUTATION_NAME.ADD_WIDGET_TRANSLATION](state, data) {
      state.widgetTranslations.translations.push(data);
    },
    [MUTATION_NAME.DELETE_WIDGET_TRANSLATION](state, data) {
      const translations = state.widgetTranslations.translations.filter(
        e => e.uuid !== data.uuid
      );
      state.widgetTranslations.translations = [...translations];
    },
    [MUTATION_NAME.ALL_WIDGET_TRANSLATIONS](state, data) {
      if (data.uuid) {
        const allTranslations = state.allWidgetTranslations;
        allTranslations[data.uuid] = data.translations || [];
        state.allWidgetTranslations = { ...allTranslations };
      }
    },
    [MUTATION_NAME.WIDGET_TRANSLATION_REQUEST](state, data) {
      if (data.uuid) {
        const translationRequest = state.widgetTranslationRequest;
        translationRequest[data.uuid] = data.request;
        state.widgetTranslationRequest = { ...translationRequest };
      }
    },
    [MUTATION_NAME.ADD_WIDGET_DATA](state, data) {
      state.addWidgetData = data;
    },
    [MUTATION_NAME.ADD_NEW_WIDGET](state, data) {
      const widgets = [...state.distributionChannelLatestWidgets.widgets];
      widgets.push(data);
      state.distributionChannelLatestWidgets.widgets = distributionChannelUtil.orderListByName(
        widgets
      );
    },
    [MUTATION_NAME.ADD_NEW_WIDGET_VERSION](state, data) {
      state.addNewWidgetVersion = data;
    },
    [MUTATION_NAME.WIDGET_VERSION_DETAILS](state, data) {
      state.widgetVersionDetails = data;
    },
    [MUTATION_NAME.ALL_WIDGET_VERSION_DETAILS](state, data) {
      if (data.uuid) {
        const allWidgetVersionDetails = state.allWidgetVersionDetails;
        allWidgetVersionDetails[data.uuid] = data.details || {};
        state.allWidgetVersionDetails = { ...allWidgetVersionDetails };
      }
    },
    [MUTATION_NAME.CUSTOMER_DOMAINS](state, data) {
      state.customerDomains = data;
    },
    [MUTATION_NAME.CUSTOMER_DOMAIN_DATA](state, data) {
      state.customerDomainData = data;
    },
    [MUTATION_NAME.ADD_CUSTOMER_DOMAIN](state, data) {
      const customers = [...state.customerDomains.customers, ...[data]];
      state.customerDomains.customers = distributionChannelUtil.orderListByName(
        customers
      );
    },
    [MUTATION_NAME.UPDATE_CUSTOMER_DOMAIN_LEVEL_ACCESS](state, data) {
      state.customerDomains.customers = state.customerDomains.customers.map(
        e => {
          if (e.uuid === data.uuid) {
            const payload = data.payload;
            e.fullAccess = payload.fullAccess;
            e.widgetAccess = payload.widgetAccess;
          }
          return e;
        }
      );
    },
    [MUTATION_NAME.DELETE_CUSTOMER_DOMAIN](state, data) {
      state.customerDomains.customers = state.customerDomains.customers.filter(
        e => e.uuid !== data
      );
    },
    [MUTATION_NAME.DISTRIBUTION_CHANNEL_LIST](state, data) {
      state.distributionChannelList = data;
    },
    [MUTATION_NAME.REFRESH_OVERVIEW](state, flag) {
      state.refreshOverview = flag;
    },
    [MUTATION_NAME.RESET_CHANNEL_PAGE_DATA](state) {
      state.allWidgetTranslations = {};
      state.widgetTranslationRequest = {};
      state.distributionChannelList = null;
      state.allWidgetVersionDetails = {};
      state.refreshOverview = true;
    },
    [MUTATION_NAME.ADD_EDIT_WIDGET_DATA](state, data) {
      state.addEditWidgetData = { ...(data || {}) };
    },
    [MUTATION_NAME.SET_IS_FETCHING_MANIFESTS](state, data) {
      state.isFetchingManifests = data;
    },
    [MUTATION_NAME.EDIT_DROPDOWN_REFERENCE](state, data) {
      state.apps = state.apps.map(app => {
        if (app.uuid === data.appId) {
          let elemId;
          if (data.fromPageDelete) {
            elemId = data.dataElementId;
          } else {
            if (app.dataElements[data.dataElementId]) {
              app.dataElements[data.dataElementId].displayElementRefCount += 1;
            }
            if (data.prevState) {
              elemId = data.prevState;
            }
          }

          if (elemId && app.dataElements[elemId]) {
            app.dataElements[elemId].displayElementRefCount -= 1;
            if (app.dataElements[elemId].displayElementRefCount === 0) {
              const params = {
                appId: data.appId,
                deId: elemId
              };

              this.commit(MUTATION_NAME.DELETE_DATA_ELEMENT, params);
            }
          }
          app.isDirty = true;
          services.updateElmCompiler(app);
        }

        return app;
      });

      services.saveAppsLocally(state.apps);
    },
  },
  getters: {
    username: state => state.username,
    userLocale: state => state.userLocale,
    userTimeZone: state => state.userTimeZone,
    user: state => state.user,
    crmServerURL: state => state.crmServerURL,
    crmServerVersion: state => state.crmServerVersion,
    mcServerURL: state => state.mcServerURL,
    mcServerVersion: state => state.mcServerVersion,
    territoryServerURL: state => state.territoryServerURL,
    territoryServerVersion: state => state.territoryServerVersion,
    domain: state => state.domain,
    org: state => state.org,
    messages: state => state.messages,
    apps: state => state.apps,
    appTypes: state => state.appTypes,
    objectNameLabels: state => state.objectNameLabels,
    htmlReportFieldLabels: state => state.htmlReportFieldLabels,
    appsDeploymentSummary: state => state.appsDeploymentSummary,
    objectDescribe: state => state.objectDescribe,
    deleteDraggableItem: state => state.deleteDraggableItem,
    editDraggableItem: state => state.editDraggableItem,
    updateDraggableItem: state => state.updateDraggableItem,
    moveDraggableItem: state => state.moveDraggableItem,
    currentContent: state => state.currentContent,
    sfGlobalObjects: state => state.sfGlobalObjects,
    sfObjectsInfo: state => state.sfObjectsInfo,
    sfEntryPointOjbectsInfo: state => state.sfEntryPointOjbectsInfo,
    sfObjectsMeta: state => state.sfObjectsMeta,
    formattedDataElement: state => {
      return validatorUtil.formatDataElement(state.editDataElement);
    },
    nitroDataCache: state => state.nitroDataCache,
    pageInfo: state => state.pageInfo,
    previewPlatform: state => state.previewPlatform,
    hasSession: state => state.hasSession,
    distributionChannelsSummary: state => state.distributionChannelsSummary,
    distributionChannelLatestWidgets: state =>
      state.distributionChannelLatestWidgets,
    distributionChannelDetail: state => state.distributionChannelDetail,
    widgetVersions: state => state.widgetVersions,
    channelWidgetVersionList: state => state.channelWidgetVersionList,
    customerDomainWidgetAccess: state => state.customerDomainWidgetAccess,
    widgetTranslations: state => state.widgetTranslations,
    allWidgetTranslations: state => state.allWidgetTranslations,
    widgetTranslationRequest: state => state.widgetTranslationRequest,
    addWidgetData: state => state.addWidgetData,
    addNewWidgetVersion: state => state.addNewWidgetVersion,
    widgetVersionDetails: state => state.widgetVersionDetails,
    allWidgetVersionDetails: state => state.allWidgetVersionDetails,
    customerDomains: state => state.customerDomains,
    customerDomainData: state => state.customerDomainData,
    setUpdateFlag: state => state.setUpdateFlag,
    distributionChannelList: state => state.distributionChannelList,
    addEditWidgetData: state => state.addEditWidgetData,
    refreshOverview: state => state.refreshOverview,
    isFetchingManifests: state => state.isFetchingManifests,
    objectLevelSecurity: state => state.objectLevelSecurity
  },
  actions: {
    [MUTATION_NAME.SET_DELETE_DRAGGABLE](context, data) {
      context.commit(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
    },
    [MUTATION_NAME.SET_EDIT_DRAGGABLE](context, data) {
      context.commit(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
    },
    [MUTATION_NAME.SET_UPDATE_DRAGGABLE](context, data) {
      context.commit(MUTATION_NAME.SET_UPDATE_DRAGGABLE, data);
    },
    [MUTATION_NAME.SET_MOVE_DRAGGABLE](context, data) {
      context.commit(MUTATION_NAME.SET_MOVE_DRAGGABLE, data);
    },
    [MUTATION_NAME.SET_SF_GLOBAL_OBJECTS](context, data) {
      context.commit(MUTATION_NAME.SET_SF_GLOBAL_OBJECTS, data);
    },
    [MUTATION_NAME.SET_SF_OBJECTS_INFO](context, data) {
      context.commit(MUTATION_NAME.SET_SF_OBJECTS_INFO, data);
    },
    [MUTATION_NAME.SET_SF_ENTRY_POINT_OBJECTS_INFO](context, data) {
      context.commit(MUTATION_NAME.SET_SF_ENTRY_POINT_OBJECTS_INFO, data);
    },
    [MUTATION_NAME.SET_SF_OBJECTS_META](context, data) {
      context.commit(MUTATION_NAME.SET_SF_OBJECTS_META, data);
    },
    [MUTATION_NAME.SET_EDIT_DATA_ELEMENT](context, data) {
      context.commit(MUTATION_NAME.SET_EDIT_DATA_ELEMENT, data);
    },
    [MUTATION_NAME.SET_NITRO_DATA_CACHE](context, data) {
      context.commit(MUTATION_NAME.SET_NITRO_DATA_CACHE, data);
    },
    [MUTATION_NAME.SET_PAGE_INFO](context, data) {
      context.commit(MUTATION_NAME.SET_PAGE_INFO, data);
    },
    [MUTATION_NAME.UPDATE_IS_DIRTY_FLAG](context, data) {
      context.commit(MUTATION_NAME.UPDATE_IS_DIRTY_FLAG, data);
    }
  },
  modules: {}
});
